// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The one renderer every arcade tile draws from (G1-12).
//
// **One painter for the whole grid, and no live renderer per tile at all**, which is the property
// FR-024's "each tile running live attract" has to be bought with. A browser caps live WebGL
// contexts at around sixteen and **evicts the oldest without asking**, so the obvious
// implementation — a renderer per tile — is a roster that can take down the machine a visitor is
// playing on the front door, because they scrolled a list.
//
// **A machine's playfield is painted once into a bitmap and kept.** Art is a procedural program
// (04 §13), deterministic given its seed, so re-running it twelve times a second per tile would
// spend the whole budget redrawing something that cannot have changed. What changes is the lamps,
// and they are a handful of filled arcs blitted over the top into each tile's own 2-D canvas — of
// which a page may have any number, because a 2-D context is not the scarce thing.
//
// So the two costs are separated deliberately, and it is the separation `attract-tiles.test.ts`
// measures: **painting is O(machines whose definition is in hand)** — one, today, and never more
// than a player has actually opened — while **the frame loop is O(the cap)**, whatever the roster
// length. A twenty-machine roster costs what a two-machine one costs.
//
// **The attract pattern is the machine's own.** Nothing here invents an animation: the lamps blink
// at the rates `lampStates.ts` computes from the codes the definition declares, at the reduced
// floor the player's own preference asks for. A thumbnail that pulsed to a rhythm this file made up
// would be a picture of a machine that does not exist.

import { renderArtSurface, type ArtSurface, type Canvas2D } from '../fx/artProgram'
import { lampBrightness, LAMP_SLOW } from '../world/lampStates'
import { toTextureUv } from '../world/lampShapes'
import type { LampKind, MachineDefinition, Point2 } from '@core/machine'

/**
 * What this painter needs, which is very much less than a machine (M8-79).
 *
 * **Named because the difference is now a route.** These four fields — the playfield's art program
 * and seed, the playfield's extents, and the lamps — are everything `prepare` and `insertsOf` read,
 * and they are what `GET /machines/{id}/versions/{semver}/art` answers with. Nothing here is a rule,
 * a device, a mode or a score, which is what lets that route be public where the definition route is
 * gated, and therefore what lets a locked tile show the machine it is offering.
 *
 * `MachineDefinition` satisfies it structurally, so the bundled flagship still goes straight in with
 * no conversion and no second code path. A type-level assertion in
 * `test/client/attract-art.test.ts` holds that true, because the day it stops being true is the day
 * `/arcade` silently splits into two ways of drawing the same picture.
 */
export interface AttractArt {
  readonly art?: MachineDefinition['art']
  /**
   * The whole dimensions block, because a cabinet is built from it (M8-81).
   *
   * `playfield` alone places an insert on a flat texture. The body's length, width and height, the
   * incline the playfield sits at and the height of the lockdown bar are what let `world/cabinet.ts`
   * build the machine a player stands at — and every one of them is optional here, because a
   * Builder machine mid-edit states half of them and still has to be drawable.
   */
  readonly dimensions: {
    readonly playfield: { readonly width: number; readonly length: number }
    readonly inclineDegrees?: number
    readonly lockdownBarHeight?: number
    readonly cabinet?: {
      readonly length?: number
      readonly width?: number
      readonly height?: number
    }
  }
  readonly lamps?: readonly AttractLamp[]
  /**
   * The mechanisms, as outlines (M8-82).
   *
   * **Every field optional and every field `unknown`, so a real `MachineDevice` satisfies it.**
   * That is the whole design: the route sends a *subset* of each device under the definition's own
   * field names, so the bundled flagship and a machine off the wire reach `partOf` as the same
   * shape and are drawn by the same code. A reduced vocabulary would have been tidier to read and
   * would have split the renderer in two.
   */
  readonly devices?: readonly AttractDevice[]
  /**
   * The drawn mass of the machine (M8-84).
   *
   * Rails come from `colliders`, ramps and habitrails are **layers**, and a toy is a toy — none of
   * them a device, which is why M8-82's tile drew a decal with some marks on it. Typed as loosely
   * as the devices are and for the same reason: a real `MachineDefinition` has to satisfy this, so
   * the bundled flagship and a machine off the wire are one shape to the renderer.
   */
  readonly layers?: readonly unknown[]
  readonly colliders?: readonly unknown[]
  readonly toys?: readonly unknown[]
}

/**
 * One mechanism, as much of it as an outline needs.
 *
 * Nothing here is a coil, a switch, a strength or an id — see `buildArtPayload` for the list and
 * the reason. `unknown` rather than `Point2` and `number` because this is what arrives off a public
 * route: `attractParts.ts` validates each field at the moment it draws it.
 */
export interface AttractDevice {
  readonly kind?: unknown
  readonly pivot?: unknown
  readonly length?: unknown
  readonly restAngle?: unknown
  readonly a?: unknown
  readonly b?: unknown
  readonly centre?: unknown
  readonly skirtRadius?: unknown
  readonly captureRadius?: unknown
  readonly region?: unknown
}

/** One lamp, as much of it as a 240-pixel-wide thumbnail can express. */
export interface AttractLamp {
  readonly kind?: LampKind
  readonly position?: Point2
  readonly size?: Point2
  readonly radius?: number
  readonly colour: string
}

/** Where a lamp sits on the thumbnail, in fractions of the surface. */
export interface Insert {
  readonly x: number
  readonly y: number
  readonly r: number
  readonly colour: string
  /** Its own phase, so a whole playfield does not blink in unison like a warning light. */
  readonly phase: number
}

/** One machine's painted playfield and the inserts drawn over it. */
interface Painted {
  /**
   * `CanvasImageSource` rather than a canvas, since M8-85.
   *
   * A picture restored from the store is a decoded `HTMLImageElement`, and the only thing this is
   * ever used for is `drawImage`, which takes either. Widening the field beat decoding into a
   * canvas the tile would then blit out of again.
   */
  readonly surface: CanvasImageSource
  readonly inserts: readonly Insert[]
}

/**
 * How large the shared surface is, in device pixels.
 *
 * **Raised from 240x420 at M8-83**, along with the tile itself. The canvas was capped at 120 CSS
 * pixels inside a tile 240 wide, so the picture used less than half the width the roster had
 * already reserved for it — and the machine is the thing somebody is choosing between, while the
 * name beside it is a name. At 360 the backing store is 1:1 with a tile filling a 240-pixel grid
 * column on an ordinary display and still sharp at the 1.5x most phones report.
 *
 * **The shape is the view's, not the cabinet's** (M8-83). A table seen down its length projects
 * wider than it is — the near edge is nearer — so `tileAspect` measures 0.94 for NEON NOVA at
 * `TILE_PITCH` and 1.04 for IRON FATHOM, against the flat playfield's 0.48. Cut at the narrower of
 * the two, so the flagship is exact and the wider machine is letterboxed by a few per cent rather
 * than everything else being surrounded by air.
 *
 * The old 0.571 frame was the *cabinet's* proportions, and no distance could have filled it: probed
 * across 40°…70°, the width bound first at every pitch, and the air that left over the top is where
 * the backbox turned up.
 *
 * Still a thumbnail, deliberately: the playfield art is drawn at 1024 in the game, and rendering
 * a roster at that size would spend a full machine's raster on each picture.
 */
export const ATTRACT_WIDTH = 360
export const ATTRACT_HEIGHT = 380

/**
 * How a machine is drawn as its player sees it (M8-81).
 *
 * `undefined` means "not this time" — no WebGL, art that would not build, a machine with no
 * playfield — and the painter falls back to the flat playfield raster it drew before.
 */
export type CabinetShotMaker = (
  art: AttractArt,
  width: number,
  height: number,
) => { readonly image: HTMLCanvasElement; readonly inserts: readonly Insert[] } | undefined

/** The default: draw no cabinet. What a test gets, and what a caller that supplies none gets. */
const noShot: CabinetShotMaker = () => undefined

/** A 2-D context factory, so a test can supply one without a browser. */
export type SurfaceFactory = (width: number, height: number) => HTMLCanvasElement | undefined

const browserSurface: SurfaceFactory = (width, height) => {
  if (typeof globalThis.document === 'undefined') return undefined
  const canvas = globalThis.document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas
}

export class AttractPainter {
  /** One painted playfield per machine, made on the first frame that needs it and then kept. */
  private readonly painted = new Map<string, Painted>()
  private readonly factory: SurfaceFactory
  /**
   * How many times a playfield has actually been rasterised.
   *
   * Counted rather than inferred, because "the art is painted once" is the claim this whole file
   * rests on and a claim about how often something happens is only checkable by counting it. The
   * test asserts this stays at one across hundreds of frames; without it a cache that silently
   * missed would look identical from the outside and cost forty rasterisations a second.
   */
  private paints = 0

  /**
   * How a machine is drawn as its player sees it, when it can be (M8-81).
   *
   * A parameter rather than an import, so every test in this file goes on running with no WebGL,
   * no three.js and no browser — and so the fallback below is exercised rather than assumed.
   */
  private readonly shoot: CabinetShotMaker

  constructor(factory: SurfaceFactory = browserSurface, shoot: CabinetShotMaker = noShot) {
    this.factory = factory
    this.shoot = shoot
  }

  /** How many machines have been painted. For the test that measures what a roster costs. */
  get cached(): number {
    return this.painted.size
  }

  /** How many rasterisations have happened in total. Never more than `cached`. */
  get rasterisations(): number {
    return this.paints
  }

  /** Is this machine painted and drawable? What the scheduler asks before listing a tile as live. */
  holds(machineId: string): boolean {
    return this.painted.has(machineId)
  }

  /**
   * Install a picture that was drawn on an earlier visit (M8-85).
   *
   * The whole point of the store: a machine adopted here is one `prepare` will not render, because
   * `prepare` is idempotent by id and finds it already held. `rasterisations` therefore stays at
   * zero for a roster that came back entirely from the store, which is the number the test asserts
   * — "it did not draw" is only checkable by counting the drawings.
   */
  adopt(machineId: string, surface: CanvasImageSource, inserts: readonly Insert[]): void {
    this.painted.set(machineId, { surface, inserts })
  }

  /**
   * The picture and the inserts, for the caller that wants to keep them.
   *
   * A canvas or nothing: a picture that came *out* of the store is an `HTMLImageElement` and there
   * is no reason to write it back. So the type is the guard — only a freshly rendered machine can
   * be saved, and re-encoding a decoded bitmap every visit is not something a caller can ask for
   * by mistake.
   */
  pictureOf(machineId: string): { surface: HTMLCanvasElement; inserts: readonly Insert[] } | undefined {
    const held = this.painted.get(machineId)
    if (held === undefined) return undefined
    const surface = held.surface as { width?: unknown; getContext?: unknown }
    if (typeof surface.getContext !== 'function') return undefined
    return { surface: held.surface as HTMLCanvasElement, inserts: held.inserts }
  }

  /**
   * Paint one machine's playfield, once, and remember it.
   *
   * Idempotent by machine id. Called when a definition arrives rather than per frame, which is the
   * whole reason the cost of a roster is the cost of the cap: painting is O(machines a player has
   * actually opened) and the frame loop is O(the cap).
   */
  prepare(machineId: string, definition: AttractArt): boolean {
    if (this.painted.has(machineId)) return true

    // **The machine, from where a player stands, when the browser will draw one** (M8-81). One
    // render, through one shared context, kept as a 2-D bitmap — see `attractCabinet.ts`. The flat
    // playfield below is what a browser with no WebGL gets, and it is why that code stays: a tile
    // showing a top-down texture is a poorer picture of a machine, not a broken one.
    const shot = this.shoot(definition, ATTRACT_WIDTH, ATTRACT_HEIGHT)
    if (shot !== undefined) {
      this.paints++
      this.painted.set(machineId, { surface: shot.image, inserts: shot.inserts })
      return true
    }

    const surface = this.factory(ATTRACT_WIDTH, ATTRACT_HEIGHT)
    if (surface === undefined) return false
    const ctx = surface.getContext('2d')
    if (ctx === null) return false
    this.paints++
    const playfield = definition.art?.playfield as ArtSurface | undefined
    if (playfield !== undefined && Array.isArray(playfield.program)) {
      // The cast `ArtEditor` makes for the same reason: `Canvas2D` is the structural subset this
      // renderer needs so that a test can hand it a recorder, and a real context is a superset of
      // it in every way but `fillStyle`'s pattern arm, which no art op ever assigns.
      renderArtSurface(ctx as unknown as Canvas2D, playfield, {
        width: ATTRACT_WIDTH,
        height: ATTRACT_HEIGHT,
        seed: definition.art?.seed ?? 1,
        // A playfield's own convention: its origin is at the bottom, where the flippers are, so
        // the geometry reads bottom-up. `renderArtSurface` flips for it rather than each op doing
        // the conversion and one of them forgetting.
        yUp: true,
      })
    }
    this.painted.set(machineId, { surface, inserts: insertsOf(definition) })
    return true
  }

  /**
   * Draw one tile's current frame.
   *
   * Everything animated is decided from `step`, which is a frame counter rather than a clock: the
   * same argument the core makes about timers, one layer up. Two tiles on the same page are handed
   * the same step and blink together *within a machine's own pattern*, which is what a room of real
   * machines in attract actually looks like.
   */
  draw(
    into: CanvasRenderingContext2D,
    machineId: string,
    step: number,
    reducedFloor: boolean,
    width: number,
    height: number,
  ): boolean {
    const held = this.painted.get(machineId)
    if (held === undefined) return false
    into.clearRect(0, 0, width, height)
    into.drawImage(held.surface, 0, 0, width, height)
    for (const insert of held.inserts) {
      // The machine's own blink rate, at the player's own floor. `LAMP_SLOW` is the attract code:
      // a machine standing idle is not running a mode, and a thumbnail strobing at a mode's rate
      // would be advertising a game that is not being played.
      const level = lampBrightness(LAMP_SLOW, step, insert.phase, reducedFloor)
      into.save()
      into.globalAlpha = 0.25 + 0.75 * level
      into.fillStyle = insert.colour
      into.beginPath()
      into.arc(insert.x * width, insert.y * height, insert.r * width, 0, Math.PI * 2)
      into.fill()
      into.restore()
    }
    return true
  }
}

/**
 * Where the inserts are, as fractions of the surface.
 *
 * Only what has a position and a radius. A flasher is a bulb *above* the playfield rather than an
 * insert in it, and a lamp that lights a region has no outline of its own — both are skipped here
 * for the same reasons `paintLampMap` skips them, rather than being drawn as a circle somewhere
 * plausible.
 *
 * Capped, because a machine may declare a great many and a thumbnail is 240 pixels wide: past a
 * couple of dozen circles the picture is a texture rather than a pattern, and every one of them is
 * an arc per frame.
 */
function insertsOf(definition: AttractArt): readonly Insert[] {
  const { width, length } = definition.dimensions.playfield
  const inserts: Insert[] = []
  for (const lamp of definition.lamps ?? []) {
    if (lamp.kind === 'flasher') continue
    const at = lamp.position
    if (at === undefined) continue
    // An oval insert has a `size` and a round one a `radius`; half the larger extent is what the
    // thumbnail draws either way, because at 240 pixels wide the difference between an oval and a
    // circle is under a pixel and carrying it would be two code paths for none.
    const extent = lamp.radius ?? (lamp.size === undefined ? undefined : Math.max(lamp.size[0], lamp.size[1]) / 2)
    // **Both axes are centred, and this line believed only one of them** (fixed M8-82).
    //
    // It read `y: 1 - at[1] / length`, which is the mapping for a playfield whose origin is its
    // *near edge*. This one's is its **centre** — `toTextureUv` in `world/lampShapes.ts` is the
    // owner, `(x + w/2)/w, (y + l/2)/l`, and the core agrees from the other direction: `Game`
    // drains at `-length/2 - 0.02` and serves at `-length/2 + 0.05`. So a lamp at the top of the
    // playfield landed at the vertical middle of the tile and everything below the centre line
    // fell off the bottom edge — about half of them, drawn off-canvas where nothing complains.
    //
    // It survived because the x term next to it is correct and because nobody has a picture of
    // where a machine's lamps *should* be at 240 pixels wide. `attract-parts.test.ts` now pins
    // both axes against the core's own mapping rather than against a reading of this line.
    const uv = toTextureUv(at, width, length)
    inserts.push({
      x: uv[0],
      // The playfield's `y` runs up the machine and a canvas draws downward, which is the same
      // flip `fillOutline` makes when it bakes the in-game insert map.
      y: 1 - uv[1],
      r: Math.max(0.012, (extent ?? 0.012) / width),
      colour: lamp.colour,
      // Spread over the blink period by index rather than randomly: a random phase is a different
      // picture on every reload of the same machine, and this is meant to be the machine.
      phase: inserts.length * 0.17,
    })
    if (inserts.length >= MAX_INSERTS) break
  }
  return inserts
}

/** How many inserts a thumbnail draws. Judgement: past this the picture is texture, not pattern. */
export const MAX_INSERTS = 24
